import React, {RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createRoot, Root} from 'react-dom/client';

import {Region} from 'wavesurfer.js/dist/plugins/regions';

import {useWavesurfer} from '@wavesurfer/react';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';
import HoverPlugin from 'wavesurfer.js/dist/plugins/hover.js';
import {getTimestampFromSeconds, ParsedTimestampWithRegions} from '@/utils/timestamp_parsing';
import {NestedComment} from '@/store/selectors/selectors';
import {alpha, Button, Drawer, MantineProvider, Menu, Popover, Stack, Text, useMantineContext, useMantineTheme} from '@mantine/core';
import {WavesurferCustomHoverLine} from './wavesurfer_custom_hover_line';
import {IconChevronLeft, IconChevronRight, IconMinus, IconPlayerPlay, IconPlayerTrackNext, IconPlus, IconQuote, IconRefresh, IconZoom, IconZoomIn} from '@tabler/icons-react';
import {generateId} from '@/store/utils/generate_id';
import {PeaksAndDuration} from '@/utils/generate_waveform_data';
import {useIsMobile} from '@/utils/user_agent';

type Props = {
    // src: string;
    duration?: PeaksAndDuration['duration'];
    peaks?: PeaksAndDuration['peaks'];
    regions: RegionWithComment[];
    onSeek: (currentTime: number) => void;
    onTimestampAction: (timestamp: string, action: 'comment') => void;
    isCurrentFile: boolean;
    isPlaying: boolean;
    height: number;
}

type SavedRegion = {
    start: number;
    end: number;
    color: string;
    content: string | HTMLElement;
}

export type RegionWithComment = {
    parsedTimestamp: ParsedTimestampWithRegions;
    comment?: NestedComment;
}

const emptyArray: number[][] = [];

export const WavesurferWithHook = (props: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [hoverValue, setHoverValue] = useState<number | null>(null);
    const hoverValueRef = useRef<number | null>(null);

    const [adhocRegions, setAdhocRegions] = useState<Region[]>([]);

    const [menuOpenAtPercent, setMenuOpenAtPercent] = useState<number | null>(null);
    const [menuOpenAtPos, setMenuOpenAtPos] = useState<{x: number, y: number} | null>(null);

    const [zoom, setZoom] = useState(1);

    const isCurrentFileRef = useRef(props.isCurrentFile);

    const isMobile = useIsMobile();
    const isMobileRef = useRef(isMobile);

    const clearAdhocRegions = () => {
        setAdhocRegions(prev => {
            if (prev.length) {
                setTimeout(() => {
                    prev[0].remove();
                }, 10);
            }

            return [];
        });
    };

    useEffect(() => {
        document.addEventListener('click', (e) => {
            clearAdhocRegions();
        });

        containerRef.current?.addEventListener('click', (e) => {
            e.stopPropagation();
        });
    }, []);

    useEffect(() => {
        isCurrentFileRef.current = props.isCurrentFile;
    }, [props.isCurrentFile]);

    const hoverPlugin = useMemo(() => {
        const hoverPlugin = HoverPlugin.create({
            // labelColor: 'green',
        });

        hoverPlugin.on('hover', (currentTime) => {
            setHoverValue(currentTime);
            hoverValueRef.current = currentTime;
        });

        hoverPlugin.on('destroy', () => {
            setHoverValue(null);
            hoverValueRef.current = null;
        });

        return hoverPlugin;
    }, []);

    const plugins = useMemo(() => [hoverPlugin], [hoverPlugin]);

    // const [peaks, setPeaks] = useState<PeaksAndDuration | null>(null);

    const peaks = {
        peaks: props.peaks,
        duration: props.duration,
    };

    // useEffect(() => {
    //     generatePeaksFromAudioSrcUrl(props.src).then(values => {
    //         setPeaks(values);
    //     });
    // }, [props.src]);

    const theme = useMantineTheme();


    const {wavesurfer, isPlaying, isReady} = useWavesurfer({
        container: containerRef as RefObject<HTMLDivElement>,
        // url: props.src,
        interact: isMobileRef.current,
        // interact: true,
        normalize: true,
        mediaControls: false,
        // mediaControls: true,
        plugins,
        peaks: peaks?.peaks ?? emptyArray,
        duration: peaks?.duration ?? 0,

        // waveColor: 'turquoise',
        // waveColor: 'hsl(190, 80%, 60%)',
        // waveColor: 'hsl(200, 90%, 70%)',
        // waveColor: 'hsl(210, 60%, 55%)',
        // waveColor: 'hsl(210, 10%, 70%)',
        // waveColor: 'hsl(210, 15%, 85%)',
        waveColor: 'hsl(190, 90%, 60%)',
        // waveColor: 'hsl(180, 90%, 65%)',

        // progressColor: 'blue',

        height: props.height,

        barWidth: 2,
        barHeight: 0.5, // the height of the wave
        // barGap: null // the optional spacing between bars of the wave, if not provided will be calculated in legacy format

    });

    const waveRef = useRef(wavesurfer);

    // useEffect(() => {
    //     let toggle = true;
    //     setInterval(() => {
    //         // console.log('setting options')
    //         waveRef.current?.setOptions({
    //             waveColor: getRandomColor(),
    //             // waveColor: toggle ? 'blue' : 'green',
    //         });
    //         toggle = !toggle;
    //     }, 500);
    // }, []);

    useEffect(() => {
        // document.addEventListener('keydown', (e) => {
        //     if (e.key === ' ') {
        //         console.log('play/pause');
        //         waveRef.current?.playPause();
        //     }
        // });
    }, []);

    const mantineTheme = useMantineTheme();

    const reactRootToUnmount = useRef<Root | null>(null);
    const wavesurferInitialized = useRef(false);
    const intervalRef = useRef<number | null>(null);

    useEffect(() => {
        // console.log('isReady', isReady);
        // if (isReady) {
        if (wavesurfer && !wavesurferInitialized.current) {
            wavesurferInitialized.current = true;

            waveRef.current = wavesurfer;
            wavesurfer!.on('timeupdate', (currentTime) => {
                props.onSeek(currentTime);
            });

            setupPlugins();

            const shadowRoot = ((containerRef.current!.childNodes[0]) as HTMLDivElement).shadowRoot!;
            const cursor = shadowRoot.querySelector('.cursor') as HTMLDivElement;
            const progress = shadowRoot.querySelector('.progress') as HTMLDivElement;

            const hoverPart = shadowRoot.querySelector('div[part="hover"]') as HTMLDivElement;
            hoverPart.style.pointerEvents = '';
            hoverPart.addEventListener('click', () => {
                // debugger;
                // props.onSeek(hoverValueRef.current! * peaks!.duration!);
            });

            if (!isMobileRef.current) {
                const hoverContainer = document.createElement('div');
                hoverPart.appendChild(hoverContainer);

                const reactRoot = createRoot(hoverContainer); // TODO: unmount on unmount

                reactRoot.render(
                    <WavesurferCustomHoverLine
                        onClickPlay={() => {
                            if (peaks.duration) {
                                props.onSeek(hoverValueRef.current! * peaks.duration);
                            }
                        }}
                        onClickMenu={(pos) => {
                            setMenuOpenAtPercent(hoverValueRef.current!);
                            setMenuOpenAtPos(pos);
                        }}
                        mantineTheme={mantineTheme}
                    />
                );
            }

            // buttons.forEach(button => {
            //     const hoverButton = document.createElement('button');
            //     hoverButton.textContent = button.text;
            //     hoverButton.style.position = 'absolute';
            //     hoverButton.style.left = '-15px';

            //     hoverButton.style.top = button.top;
            //     hoverButton.style.cursor = 'pointer';

            //     hoverButton.addEventListener('click', () => {
            //         button.cb();
            //     });
            //     hoverPart.appendChild(hoverButton);
            // });

            if (!isMobileRef.current) {
                regionsPlugin.current?.enableDragSelection({
                    color: 'rgba(255, 255, 0, 0.1)',
                    drag: true,
                    resize: true,
                });
            }

            regionsPlugin.current?.on('region-clicked', (region, e) => {
                // console.log('region-clicked', region, e);
            });

            // regionsPlugin.enableDragSelection({

            // })
            regionsPlugin.current?.on('region-created', e => {
                // e.remove();
                // e.drag = false;
                e.drag = true;
                e.resize = true;
                setTimeout(() => {
                    // props.onSeek(e.start);
                    // e.remove();
                    // e.play();
                }, 10);

                if (e.id.includes('automated')) {
                    return;
                }

                setAdhocRegions(prev => {
                    // console.log('setAdhocRegions 1');
                    if (prev.length) {
                        setTimeout(() => {
                            prev[0]?.remove();
                        }, 10);
                    }

                    return [e];
                });
            })

            cursorRef.current = cursor;
            progressRef.current = progress;
        }
    }, [wavesurfer]);

    const cursorRef = useRef<HTMLDivElement | null>(null);
    const progressRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (props.isPlaying) {
            intervalRef.current = setInterval(() => {
                if (!isCurrentFileRef.current) {
                    return;
                }

                const audio = (window as any).audioElement as HTMLAudioElement | undefined;
                if (audio && audio.currentTime !== 0 && cursorRef.current && progressRef.current && waveRef.current) {
                    cursorRef.current.style.left = `${(audio.currentTime / waveRef.current.getDuration()) * 100}%`;
                    progressRef.current.style.width = `${(audio.currentTime / waveRef.current.getDuration()) * 100}%`;
                }
            });

            return () => {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
            };
        }
    }, [props.isPlaying]);

    useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }

            if (reactRootToUnmount.current) {
                reactRootToUnmount.current.unmount();
            }
        };
    }, []);


    // const onZoomChange = useCallback(
    //     (e) => {
    //         if (!wavesurfer) return;
    //         wavesurfer.zoom(newValue as number);
    //     },
    //     [wavesurfer]
    // );

    // const [dragEnabled, setDragEnabled] = useState(true);
    // const dragEnabledRef = useRef(true);

    const regionsPlugin = useRef<RegionsPlugin | null>(null);

    const [savedRegions, setSavedRegions] = useState<SavedRegion[]>([]);

    useEffect(() => {
        // console.log('setSavedRegions because props.regions changed')
        let numChordRegions = 0;
        setSavedRegions(props.regions.map((region, i) => {
            const isChordRegion = !region.comment;

            if (isChordRegion) {
                numChordRegions++;
            }

            let color = '';
            if (isChordRegion) {
                color = numChordRegions % 2 === 0 ? alpha('var(--mantine-color-blue-filled)', 0.3) : alpha('var(--mantine-color-green-filled)', 0.26);
            } else {
                color = alpha('var(--mantine-color-green-filled)', 0.26);
                // const anyChordRegions = props.regions.some(r => !r.comment);
                // color = anyChordRegions ? alpha('var(--mantine-color-red-filled)', 0.3) : alpha('var(--mantine-color-green-filled)', 0.26);
            }

            if (region.parsedTimestamp.kind === 'point') {
                return {
                    start: region.parsedTimestamp.time + 0.1,
                    end: region.parsedTimestamp.time + 1 - 0.1,
                    color,
                    content: '',
                    // content: createRegionContent(region.parsedTimestamp.text),
                };
            } else {
                return {
                    start: region.parsedTimestamp.start + 0.1,
                    end: region.parsedTimestamp.end - 0.1,
                    color,
                    content: '',
                    // content: createRegionContent(region.parsedTimestamp.text),
                };
            }
        }));
    }, [props.regions]);

    const mounted = useRef(false);

    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            return;
        }

        if (savedRegions.length > 0 && regionsPlugin.current) {
            setupPlugins();
            if (adhocRegions.length) {
                // adhocRegions[0]?.remove();
                setAdhocRegions([]);
            }
        }
    }, [savedRegions]);

    const setupPlugins = () => {
        toggleDrag();
    };

    const toggleDrag = () => {
        if (isMobile) {
            return;
        }

        if (regionsPlugin.current) {
            regionsPlugin.current.clearRegions();
        } else {
            regionsPlugin.current = RegionsPlugin.create();
            wavesurfer?.registerPlugin(regionsPlugin.current);
        }

        // if (!dragEnabled) {
        // regionsPlugin.current.enableDragSelection({
        //     // color: 'gold'
        // });
        // }

        // setDragEnabled(!dragEnabled);
        // dragEnabledRef.current = !dragEnabled;

        for (const region of savedRegions) {
            regionsPlugin.current.addRegion({...region, drag: true, resize: true, id: `region-${generateId().substring(0, 8)}-automated`});
        }

        // regionsPlugin.current.on('region-created', region => {
        //     if (!dragEnabledRef.current) {
        //         return;
        //     }


        //     console.log('region created');
        //     // e.remove();
        //     region.drag = false;
        //     // setTimeout(() => {
        //     //     // e.remove();
        //     //     e.play();
        //     // }, 10);
        //     // console.log(e);
        //     setSavedRegions(existing => [...existing, {
        //         start: region.start,
        //         end: region.end,
        //         color: '',
        //     }]);
        // })
    };

    const getDropDownPositions = () => {
        const container = containerRef.current!;

        const rect = container.getBoundingClientRect();
        const boxLeft = rect.left;
        const boxTop = rect.top;
        const boxWidth = rect.width;
        const boxHeight = rect.height;

        const regionStart = adhocRegions[0].start;
        const regionEnd = adhocRegions[0].end;
        const regionDuration = regionEnd - regionStart;
        const totalDuration = wavesurfer!.getDuration();
        const middleOfRegion = regionStart + regionDuration / 2;
        const positionRatio = middleOfRegion / totalDuration;
        const offsetFromLeft = boxWidth * positionRatio;

        const left = boxLeft + offsetFromLeft - 50; // 50 to account for half of width of popover

        const scrollY = window.scrollY || window.pageYOffset;
        const absoluteTop = boxTop + scrollY;

        return {
            left,
            top: absoluteTop + boxHeight + 10,
        };
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                // width: "100%",
                // height: "100%",
            }}
        >
            <div ref={containerRef} className="Container wavesurfer-container" style={{marginBottom: '5px'}} />

            {Boolean(adhocRegions.length) && (
                <Popover
                    opened={true}
                    classNames={{
                        dropdown: 'adhoc-regions-popover',
                    }}
                    // onClose={() => {
                    //     setAdhocRegions([]);
                    // }}
                    // onDismiss={() => {
                    //     setAdhocRegions([]);
                    // }}
                    closeOnClickOutside={false}
                    closeOnEscape={false}
                    position='bottom'
                    withArrow
                    offset={4}
                    withinPortal={false}
                    styles={{
                        dropdown: {
                            position: 'absolute',
                            zIndex: 1000,
                            ...getDropDownPositions(),
                        },
                    }}
                >
                    <Popover.Target>
                        <div style={{
                            width: 1,
                            height: 1,
                            position: 'absolute',
                            pointerEvents: 'none'
                        }} />
                    </Popover.Target>
                    <Popover.Dropdown
                        style={{
                            // width: 20,
                            padding: 0,
                        }}
                    >
                        <Stack
                            gap={'xs'}
                            align='flex-start'
                            justify='flex-start'
                        >
                            <div>

                                <Button
                                    variant="default"
                                    size="xs"
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        const start = adhocRegions[0].start;
                                        const end = adhocRegions[0].end;
                                        const timestamp = getTimestampFromSeconds(start) + ' - ' + getTimestampFromSeconds(end);

                                        props.onTimestampAction(timestamp, 'comment');
                                        // adhocRegions[0].remove();
                                        // console.log('setAdhocRegions 2');
                                        // setAdhocRegions([]);
                                    }}
                                >
                                    <IconQuote
                                        style={{cursor: 'pointer'}}
                                        size={20}
                                    />
                                </Button>
                                <Button
                                    variant="default"
                                    size="xs"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        props.onSeek(adhocRegions[0].start);
                                    }}
                                >
                                    <IconPlayerPlay
                                        style={{cursor: 'pointer'}}
                                        size={20}
                                    />
                                </Button>
                            </div>


                            {/* <div>
                                <Button
                                    variant="default"
                                    size="xs"
                                >
                                    <IconChevronLeft
                                        style={{cursor: 'pointer'}}
                                        size={20}
                                        onClick={() => {

                                        }}
                                    />
                                </Button>
                                <Button
                                    variant="default"
                                    size="xs"
                                >
                                    <IconChevronRight
                                        style={{cursor: 'pointer'}}
                                        size={20}
                                        onClick={() => {

                                        }}
                                    />
                                </Button>
                            </div> */}


                            {/* <Button
                                variant="default"
                                size="xs"
                            >
                                <IconZoomIn
                                    style={{cursor: 'pointer'}}
                                    size={20}
                                    onClick={() => {

                                    }}
                                />
                            </Button> */}
                            {/* <IconPlus
                                size={10}
                                onClick={() => {
                                    setZoom(zoom * 2);
                                    wavesurfer!.zoom(zoom * 2);
                                }}
                            />
                            <IconMinus
                                size={10}
                                onClick={() => {
                                    setZoom(zoom / 2);
                                    wavesurfer!.zoom(zoom / 2);
                                }}
                            /> */}
                        </Stack>
                    </Popover.Dropdown>
                </Popover>
            )}

            {/* {menuOpenAtPercent !== null && (
                <Popover
                    opened={true}
                    onClose={() => {
                        setMenuOpenAtPercent(null);
                        setMenuOpenAtPos(null);
                    }}
                    onDismiss={() => {
                        setMenuOpenAtPercent(null);
                        setMenuOpenAtPos(null);
                    }}
                    position="top"
                    // withArrow
                    offset={4}
                    withinPortal={false}
                    styles={{
                        dropdown: {
                            position: 'absolute',
                            left: menuOpenAtPos!.x - 25,
                            top: menuOpenAtPos!.y + 10,
                            zIndex: 1000,
                        },
                    }}
                >
                    <Popover.Target>
                        <div style={{width: 1, height: 1}} />
                    </Popover.Target>
                    <Popover.Dropdown
                        style={{
                            // width: 20,
                        }}
                    >
                        <Stack spacing="xs">
                            <IconQuote
                                size={10}
                                onClick={() => {
                                    props.onTimestampAction(menuOpenAtPercent! * wavesurfer!.getDuration(), 'comment');
                                }}
                            />
                            <IconPlus
                                size={10}
                                onClick={() => {
                                    setZoom(zoom * 2);
                                    wavesurfer!.zoom(zoom * 2);
                                }}
                            />
                            <IconMinus
                                size={10}
                                onClick={() => {
                                    setZoom(zoom / 2);
                                    wavesurfer!.zoom(zoom / 2);
                                }}
                            />
                        </Stack>
                    </Popover.Dropdown>
                </Popover>
            )} */}
            <style>
                {
                    `
                        div[part='region-handle'] {display: none}
                    `
                }
            </style>
            {/* <div style={{width: "300px", padding: "16px"}}>
                <input
                    type='range'
                    onChange={(e) => {
                        wavesurfer?.zoom(parseInt(e.target.value))
                    }}
                    min={0}
                    max={150}
                />
            </div> */}
            {/* <button
                type='button'
                onClick={toggleDrag}
            >
                Toggle drag
            </button>
            <div>
                Drag enabled: {dragEnabled.toString()}
            </div>
            <pre>
                {JSON.stringify(savedRegions, null, 2)}
            </pre> */}
        </div>
    );
};

function getRandomColor() {
    var letters = '0123456789ABCDEF';
    var color = '#';
    for (var i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
}

function createRegionContent(message: string) {
    const contentDiv = document.createElement('div');
    contentDiv.innerText = message;

    // Base styles: invisible text
    contentDiv.style.opacity = '1';
    contentDiv.style.transition = 'opacity 0.2s ease';
    contentDiv.style.pointerEvents = 'none'; // prevent it from interfering with hover unless you want it clickable
    contentDiv.style.userSelect = 'none';
    // contentDiv.style.zIndex = '1000';

    contentDiv.style.position = 'absolute';
    // contentDiv.style.bottom = '-10px';
    // contentDiv.style.top = '100%'; // place directly below the region
    contentDiv.style.left = '5px';
    contentDiv.style.width = '100%';
    // contentDiv.style.marginTop = '4px';
    // contentDiv.style.textAlign = 'center'; // optional
    contentDiv.style.pointerEvents = 'none'; // let mouse events pass through
    contentDiv.style.fontSize = '10px';
    // contentDiv.style.color = 'black';

    // Container to handle hover
    const wrapperDiv = document.createElement('div');
    wrapperDiv.style.width = '100%';
    wrapperDiv.style.height = '100%';
    wrapperDiv.style.display = 'flex';
    // wrapperDiv.style.alignItems = 'center';
    // wrapperDiv.style.justifyContent = 'center';
    wrapperDiv.style.position = 'relative';

    // Attach hover handlers on wrapper
    wrapperDiv.addEventListener('mouseenter', () => {
        // contentDiv.style.opacity = '1';
    });
    wrapperDiv.addEventListener('mouseleave', () => {
        // contentDiv.style.opacity = '0';
    });

    wrapperDiv.appendChild(contentDiv);
    return wrapperDiv;
}
